import React, { useState } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Plane, 
  FileText, 
  Building2, 
  Landmark, 
  FileCheck, 
  Calculator,
  Check,
  Clock,
  Globe,
  Shield,
  Users,
  Award,
  Phone,
  Mail,
  MapPin,
  ChevronDown,
  Menu,
  X,
  MessageCircle,
  TrendingUp,
  Home
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hero Section
const HeroSection = () => {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section 
      data-testid="hero-section"
      className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-black"
    >
      {/* Background Image - Burj Khalifa style */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
        }}
        role="img"
        aria-label="Dubai skyline with Burj Khalifa - UAE Golden Visa services"
      ></div>
      
      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto pt-20">
        {/* USP Badge */}
        <div className="inline-flex items-center gap-2 bg-[#E8A620]/20 border border-[#E8A620]/40 rounded-full px-4 py-2 mb-6 animate-fade-in-up">
          <Clock className="text-[#E8A620]" size={16} />
          <span className="text-[#E8A620] font-medium text-sm">Get your Golden Visa Processed in 1 Day</span>
        </div>
        
        <h1 className="text-5xl sm:text-6xl lg:text-8xl font-semibold tracking-tight mb-6 animate-fade-in-up leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
          <span className="text-white">Make Dubai your </span>
          <span className="text-[#E8A620] italic">permanent</span>
          <br />
          <span className="text-[#E8A620] italic">home</span>
        </h1>
        
        <p className="text-lg sm:text-xl text-[#9CA3AF] italic mb-10 animate-fade-in-up stagger-2">
          Elite Access. Effortless Experience.
        </p>
        
        <button 
          data-testid="hero-cta-btn"
          onClick={scrollToContact}
          className="bg-white text-black font-medium px-8 py-4 rounded-full text-lg hover:bg-gray-100 transition-all duration-300 animate-fade-in-up stagger-3 inline-flex items-center gap-2"
        >
          Book a free consultation
          <span className="text-xl">→</span>
        </button>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <ChevronDown className="text-[#E8A620]" size={32} />
      </div>
    </section>
  );
};

// Stats Bar
const StatsBar = () => {
  return (
    <section data-testid="stats-section" className="relative z-20 px-6 py-12 bg-black">
      <div className="max-w-4xl mx-auto">
        {/* Tagline */}
        <p className="text-center text-[#9CA3AF] text-lg mb-8 italic">
          Your trusted partner for everything in UAE!
        </p>
        
        {/* Stats */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#E8A620] flex items-center justify-center">
              <Check className="text-black" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">4000+</p>
              <p className="text-sm text-[#9CA3AF]">Applications Processed</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#E8A620] flex items-center justify-center">
              <Clock className="text-black" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">24/7</p>
              <p className="text-sm text-[#9CA3AF]">Available</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#E8A620] flex items-center justify-center">
              <Award className="text-black" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">100%</p>
              <p className="text-sm text-[#9CA3AF]">Elite Services</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: <Plane className="text-[#E8A620]" size={32} />,
      title: "Visas",
      description: "Golden Visa, Investor Visa, Retirement Visa, Employment Visa, and all UAE residency solutions with 1-day processing available."
    },
    {
      icon: <FileText className="text-[#E8A620]" size={32} />,
      title: "Wills",
      description: "Professional will registration services including DIFC Wills, Abu Dhabi Wills, ADGM Wills, guardianship arrangements, asset protection planning, and comprehensive estate planning services to secure your family's future in the UAE."
    },
    {
      icon: <Building2 className="text-[#E8A620]" size={32} />,
      title: "Company Formation",
      description: "Complete business setup services including Mainland LLC, Free Zone company formation, Offshore companies, trade license acquisition, and PRO services for hassle-free establishment."
    },
    {
      icon: <Landmark className="text-[#E8A620]" size={32} />,
      title: "Bank Account Setup",
      description: "Personal and corporate bank account opening with premium UAE banks, business banking solutions, multi-currency accounts, and relationship management services."
    },
    {
      icon: <FileCheck className="text-[#E8A620]" size={32} />,
      title: "Document Attestation",
      description: "Complete document legalization services including Embassy attestation, MOFA attestation, certificate verification, translation services, and apostille services."
    },
    {
      icon: <Calculator className="text-[#E8A620]" size={32} />,
      title: "Corporate Tax & Accounting",
      description: "VAT registration and filing, corporate tax compliance, bookkeeping, financial reporting, audit support, and professional accounting services for businesses of all sizes."
    },
  ];

  return (
    <section data-testid="services-section" className="py-24 sm:py-32 px-6 bg-[#0a0a0a]" id="services">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#E8A620] mb-4">Our Verticals</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Premium Services for Your<br />
            <span className="text-[#E8A620] italic">UAE Journey</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div 
              key={index}
              data-testid={`service-card-${index}`}
              className="bg-[#111111] border border-[#E8A620]/20 rounded-xl p-8 card-hover animate-fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="mb-6">{service.icon}</div>
              <h3 className="text-2xl font-semibold mb-3 text-white">{service.title}</h3>
              <p className="text-[#9CA3AF] leading-relaxed">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Benefits Section - Three Pathways
const BenefitsSection = () => {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const visaPathways = [
    {
      icon: <Award className="text-[#E8A620]" size={32} />,
      title: "Golden Visa",
      description: "Achieve permanent residency in the UAE with our premium Golden Visa service. Live, work, and invest without restrictions while enjoying complete freedom.",
    },
    {
      icon: <TrendingUp className="text-[#E8A620]" size={32} />,
      title: "Investor Visa",
      description: "2-Year UAE Property Investor Visa. Available to individuals who own property in the UAE valued at AED 750,000 or more.",
    },
    {
      icon: <Home className="text-[#E8A620]" size={32} />,
      title: "Retirement Visa",
      description: "For applicants 55 years or older who meet the financial requirements. Enjoy your golden years in luxury with complete peace of mind.",
    },
  ];

  return (
    <section 
      data-testid="benefits-section"
      className="py-24 sm:py-32 px-6 bg-black"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-white">
            Three pathways to your new life
          </h2>
          <p className="text-lg text-[#9CA3AF] italic">
            Choose the visa that aligns with your vision for the future
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {visaPathways.map((visa, index) => (
            <div 
              key={index}
              data-testid={`visa-pathway-${index}`}
              className="bg-transparent border border-[#333333] rounded-xl p-8 hover:border-[#E8A620]/40 transition-all duration-300"
            >
              <div className="mb-6">{visa.icon}</div>
              <h3 className="text-2xl font-semibold mb-4 text-white">{visa.title}</h3>
              <p className="text-[#9CA3AF] leading-relaxed mb-6">{visa.description}</p>
              <button 
                onClick={scrollToContact}
                className="text-[#E8A620] font-medium inline-flex items-center gap-2 hover:gap-3 transition-all"
              >
                Explore this visa
                <span>→</span>
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us
const WhyChooseUs = () => {
  const reasons = [
    {
      number: "01",
      title: "Elite VVIP Service",
      description: "Dedicated relationship managers ensuring personalized attention for every client."
    },
    {
      number: "02",
      title: "Hassle-Free Process",
      description: "We handle all paperwork, appointments, and government liaisons on your behalf."
    },
    {
      number: "03",
      title: "1 Day Processing",
      description: "Industry-leading turnaround time for Golden Visa applications and approvals."
    },
  ];

  return (
    <section data-testid="why-choose-section" className="py-24 sm:py-32 px-6 bg-[#0a0a0a]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#E8A620] mb-4">Why Choose Us</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Your Trusted Partner for<br />
            <span className="text-[#E8A620] italic">UAE Residency</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <div 
              key={index}
              data-testid={`reason-card-${index}`}
              className="text-center animate-fade-in-up"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <p className="text-6xl font-bold text-[#E8A620]/30 mb-4">{reason.number}</p>
              <h3 className="text-2xl font-semibold mb-3 text-white">{reason.title}</h3>
              <p className="text-[#9CA3AF] leading-relaxed">{reason.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Eligibility Section
const EligibilitySection = () => {
  const categories = [
    {
      title: "Property Investors",
      requirements: [
        "Own UAE properties worth AED 2 million or more",
        "4% DLD fee and down payment completed",
        "Property can be under mortgage"
      ]
    },
    {
      title: "Executive Directors & Managers",
      requirements: [
        "Minimum 2 years experience with current company",
        "Salary of AED 30,000+ per month",
        "Ministry of Education Equivalency Certificate"
      ]
    },
    {
      title: "Entrepreneurs",
      requirements: [
        "AED 2,000,000+ in any UAE investment fund",
        "Company paying taxes no less than AED 250,000+",
        "Investor in any UAE Enterprise"
      ]
    },
    {
      title: "Retirement Visa",
      requirements: [
        "5 years validity",
        "Minimum Investment: AED 1 Million",
        "Age: 55 years or older",
        "4% DLD fee and down payment completed"
      ]
    },
  ];

  return (
    <section data-testid="eligibility-section" className="py-24 sm:py-32 px-6 bg-black" id="eligibility">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#E8A620] mb-4">Eligibility</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Golden Visa<br />
            <span className="text-[#E8A620] italic">Eligibility Criteria</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {categories.map((category, index) => (
            <div 
              key={index}
              data-testid={`eligibility-card-${index}`}
              className="bg-[#111111] border border-[#E8A620]/20 rounded-xl p-8 card-hover animate-fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <h3 className="text-2xl font-semibold mb-4 text-[#E8A620]">{category.title}</h3>
              <ul className="space-y-3">
                {category.requirements.map((req, reqIndex) => (
                  <li key={reqIndex} className="flex items-start gap-3">
                    <Check className="text-[#E8A620] flex-shrink-0 mt-1" size={18} />
                    <span className="text-[#9CA3AF]">{req}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+971',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const countryCodes = [
    { code: '+971', country: 'UAE' },
    { code: '+1', country: 'USA' },
    { code: '+44', country: 'UK' },
    { code: '+91', country: 'India' },
    { code: '+92', country: 'Pakistan' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+974', country: 'Qatar' },
    { code: '+973', country: 'Bahrain' },
    { code: '+965', country: 'Kuwait' },
    { code: '+968', country: 'Oman' },
    { code: '+20', country: 'Egypt' },
    { code: '+962', country: 'Jordan' },
    { code: '+961', country: 'Lebanon' },
    { code: '+86', country: 'China' },
    { code: '+49', country: 'Germany' },
    { code: '+33', country: 'France' },
    { code: '+39', country: 'Italy' },
    { code: '+7', country: 'Russia' },
    { code: '+61', country: 'Australia' },
    { code: '+81', country: 'Japan' },
    { code: '+82', country: 'South Korea' },
    { code: '+65', country: 'Singapore' },
    { code: '+60', country: 'Malaysia' },
    { code: '+63', country: 'Philippines' },
    { code: '+27', country: 'South Africa' },
    { code: '+234', country: 'Nigeria' },
    { code: '+254', country: 'Kenya' },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Combine country code with phone
    const fullPhone = `${formData.countryCode}${formData.phone}`;

    try {
      // Send to Formspree for email notification
      await axios.post('https://formspree.io/f/mwvalewa', {
        name: formData.name || 'Not provided',
        email: formData.email || 'Not provided',
        phone: fullPhone,
        message: formData.message || 'Contact request',
        _subject: `New Inquiry from ${formData.name || 'Website Visitor'} - North Star Consulting`
      });

      // Also save to backend database
      await axios.post(`${API}/contact`, {
        name: formData.name || 'Not provided',
        email: formData.email || 'not-provided@example.com',
        phone: fullPhone,
        message: formData.message || 'Contact request'
      });

      setSubmitStatus({ 
        type: 'success', 
        message: 'Thank you for your inquiry! Our team will get back to you within 24 hours.' 
      });
      setFormData({ name: '', email: '', countryCode: '+971', phone: '', message: '' });
    } catch (error) {
      // Even if backend fails, if Formspree worked, show success
      if (error.response?.status !== 400) {
        setSubmitStatus({ 
          type: 'success', 
          message: 'Thank you for your inquiry! Our team will get back to you within 24 hours.' 
        });
        setFormData({ name: '', email: '', countryCode: '+971', phone: '', message: '' });
      } else {
        setSubmitStatus({ 
          type: 'error', 
          message: 'Something went wrong. Please try again or contact us via WhatsApp.' 
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section data-testid="contact-section" className="py-24 sm:py-32 px-6 bg-[#0a0a0a]" id="contact">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#E8A620] mb-4">Get In Touch</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6 text-white">
              Start Your<br />
              <span className="text-[#E8A620] italic">UAE Journey Today</span>
            </h2>
            <p className="text-[#9CA3AF] mb-8 leading-relaxed">
              Ready to make Dubai your permanent home? Contact our team for a free consultation 
              and discover how we can help you achieve your UAE residency goals.
            </p>

            <div className="space-y-6">
              <a 
                href="tel:+971585916890"
                data-testid="contact-phone-link"
                className="flex items-center gap-4 text-[#9CA3AF] hover:text-[#E8A620] transition-colors"
              >
                <div className="w-12 h-12 bg-[#111111] border border-[#E8A620]/20 rounded-lg flex items-center justify-center">
                  <Phone className="text-[#E8A620]" size={20} />
                </div>
                <span>+971 58 591 6890</span>
              </a>

              <a 
                href="mailto:hello@northstarconsulting.ae"
                data-testid="contact-email-link"
                className="flex items-center gap-4 text-[#9CA3AF] hover:text-[#E8A620] transition-colors"
              >
                <div className="w-12 h-12 bg-[#111111] border border-[#E8A620]/20 rounded-lg flex items-center justify-center">
                  <Mail className="text-[#E8A620]" size={20} />
                </div>
                <span>hello@northstarconsulting.ae</span>
              </a>

              <a 
                href="https://wa.me/971585916890"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-center gap-4 text-[#9CA3AF] hover:text-[#25D366] transition-colors"
              >
                <div className="w-12 h-12 bg-[#111111] border border-[#25D366]/20 rounded-lg flex items-center justify-center">
                  <MessageCircle className="text-[#25D366]" size={20} />
                </div>
                <span>Chat on WhatsApp</span>
              </a>

              {/* Business Hours */}
              <div className="flex items-center gap-4 text-[#9CA3AF]">
                <div className="w-12 h-12 bg-[#111111] border border-[#E8A620]/20 rounded-lg flex items-center justify-center">
                  <Clock className="text-[#E8A620]" size={20} />
                </div>
                <div>
                  <p className="text-white font-medium">Business Hours</p>
                  <p className="text-sm">Monday to Saturday: 8 AM - 4 PM</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#111111] border border-[#E8A620]/20 rounded-2xl p-8">
            <h3 className="text-2xl font-semibold mb-6 text-white">Request Free Consultation</h3>
            
            {submitStatus && (
              <div 
                data-testid="form-status-message"
                className={`p-4 rounded-lg mb-6 ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-900/30 border border-green-500/30 text-green-400' 
                    : 'bg-red-900/30 border border-red-500/30 text-red-400'
                }`}
              >
                {submitStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  name="name"
                  data-testid="contact-name-input"
                  placeholder="Your Name (Optional)"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  data-testid="contact-email-input"
                  placeholder="Email Address (Optional)"
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <select
                  name="countryCode"
                  data-testid="contact-country-code"
                  value={formData.countryCode}
                  onChange={handleChange}
                  className="input-field cursor-pointer col-span-1"
                >
                  {countryCodes.map((cc) => (
                    <option key={cc.code} value={cc.code}>
                      {cc.code} {cc.country}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="phone"
                  data-testid="contact-phone-input"
                  placeholder="Phone Number *"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="input-field col-span-2"
                />
              </div>
              <div>
                <textarea
                  name="message"
                  data-testid="contact-message-input"
                  placeholder="How can we help you? (Optional)"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="input-field resize-none"
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={isSubmitting}
                className="w-full bg-[#E8A620] text-black font-semibold py-4 rounded-lg hover:bg-[#f0b530] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
              </button>
              <p className="text-xs text-[#9CA3AF] text-center">* Phone number is required</p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What is the minimum amount to invest for a 10-year property Investor Visa in the UAE?",
      answer: "You must invest at least 2 million AED in Dubai to be eligible for the 10-year property Investor Visa."
    },
    {
      question: "Is a 10-year Investor Visa the same as having a Golden Visa in Dubai?",
      answer: "Yes, a 10-year investor Visa is considered a Golden Visa in Dubai."
    },
    {
      question: "Can I sponsor my family with a UAE Investor Visa?",
      answer: "Yes, if you have a property investor Visa in the UAE, you can sponsor your parents, spouse and children for a period of 10-years."
    },
    {
      question: "What are the different types of Investor Visas in the UAE?",
      answer: "The following are the several types of investor visas available in the UAE:\n\n• 10-year Investor Visa\n• 5-year Retirement Visa\n• 2-year Investor Visa"
    },
    {
      question: "Can I work in Dubai in any company with an investor visa?",
      answer: "Yes, holders of a Dubai investor Visa may live and work in Dubai. MOHRE permission is required."
    },
    {
      question: "Do I need to visit Dubai every 6 months to keep my investor Visa valid?",
      answer: "No, you can stay unlimited time outside UAE."
    },
    {
      question: "Is it possible to apply for Investor Visa, if the person is outside the country?",
      answer: "If a person is outside the country, he cannot apply for an investor Visa since his physical presence is required within the country throughout the completion of the residency procedures, and he is not permitted to travel during the procedure."
    },
    {
      question: "Can I use more than one property title deed to obtain real estate investor residency? For parents.",
      answer: "Yes, but the total value of the property must not be less than 2 million AED."
    },
  ];

  return (
    <section data-testid="faq-section" className="py-24 sm:py-32 px-6 bg-black" id="faq">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#E8A620] mb-4">FAQ</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Frequently Asked<br />
            <span className="text-[#E8A620] italic">Questions</span>
          </h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              data-testid={`faq-item-${index}`}
              className="bg-[#111111] border border-[#E8A620]/20 rounded-xl overflow-hidden"
            >
              <button
                data-testid={`faq-btn-${index}`}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-[#1A1A1A] transition-colors"
              >
                <span className="text-lg font-medium pr-4 text-white">{faq.question}</span>
                <ChevronDown 
                  className={`text-[#E8A620] flex-shrink-0 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`} 
                  size={24} 
                />
              </button>
              {openIndex === index && (
                <div className="px-6 pb-6">
                  <p className="text-[#9CA3AF] leading-relaxed whitespace-pre-line">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Header
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeNav, setActiveNav] = useState('Home');

  const scrollTo = (id, navItem) => {
    if (id === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveNav(navItem);
    setIsMenuOpen(false);
  };

  return (
    <header 
      data-testid="header"
      className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm"
    >
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <a href="/" data-testid="logo-link" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#E8A620] flex items-center justify-center">
            <span className="text-black font-bold text-sm">N</span>
          </div>
          <span className="text-white font-medium tracking-wide">NORTH STAR</span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          <button 
            onClick={() => scrollTo('top', 'Home')} 
            className={`transition-colors text-sm ${activeNav === 'Home' ? 'text-[#E8A620]' : 'text-white hover:text-[#E8A620]'}`}
          >
            Home
          </button>
          <button 
            onClick={() => scrollTo('services', 'Visa')} 
            className={`transition-colors text-sm ${activeNav === 'Visa' ? 'text-[#E8A620]' : 'text-white hover:text-[#E8A620]'}`}
          >
            Visa
          </button>
          <button 
            onClick={() => scrollTo('services', 'Other Services')} 
            className={`transition-colors text-sm ${activeNav === 'Other Services' ? 'text-[#E8A620]' : 'text-white hover:text-[#E8A620]'}`}
          >
            Other Services
          </button>
          <button 
            onClick={() => scrollTo('faq', 'FAQs')} 
            className={`transition-colors text-sm ${activeNav === 'FAQs' ? 'text-[#E8A620]' : 'text-white hover:text-[#E8A620]'}`}
          >
            FAQs
          </button>
          <button 
            onClick={() => scrollTo('contact', 'Contact Us')} 
            className={`transition-colors text-sm ${activeNav === 'Contact Us' ? 'text-[#E8A620]' : 'text-white hover:text-[#E8A620]'}`}
          >
            Contact Us
          </button>
          <a 
            href="https://wa.me/971585916890"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-btn"
            className="bg-white text-black font-medium py-2 px-5 rounded-full text-sm flex items-center gap-2 hover:bg-gray-100 transition-colors"
          >
            <Phone size={16} />
            WhatsApp Us
          </a>
        </nav>

        {/* Mobile Menu Button */}
        <button 
          data-testid="mobile-menu-btn"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden text-white"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      {isMenuOpen && (
        <nav className="md:hidden bg-black border-t border-white/10 p-6 space-y-4">
          <button 
            onClick={() => scrollTo('top', 'Home')} 
            className="block w-full text-left text-white hover:text-[#E8A620] transition-colors"
          >
            Home
          </button>
          <button 
            onClick={() => scrollTo('services', 'Visa')} 
            className="block w-full text-left text-white hover:text-[#E8A620] transition-colors"
          >
            Visa
          </button>
          <button 
            onClick={() => scrollTo('services', 'Other Services')} 
            className="block w-full text-left text-white hover:text-[#E8A620] transition-colors"
          >
            Other Services
          </button>
          <button 
            onClick={() => scrollTo('faq', 'FAQs')} 
            className="block w-full text-left text-white hover:text-[#E8A620] transition-colors"
          >
            FAQs
          </button>
          <button 
            onClick={() => scrollTo('contact', 'Contact Us')} 
            className="block w-full text-left text-white hover:text-[#E8A620] transition-colors"
          >
            Contact Us
          </button>
          <a 
            href="https://wa.me/971585916890"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-black font-medium py-3 px-5 rounded-full text-sm flex items-center justify-center gap-2 w-full"
          >
            <Phone size={16} />
            WhatsApp Us
          </a>
        </nav>
      )}
    </header>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-black border-t border-[#E8A620]/10 py-16 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-[#E8A620] flex items-center justify-center">
                <span className="text-black font-bold text-sm">N</span>
              </div>
              <span className="text-lg font-medium tracking-wide text-white">North Star Consulting</span>
            </div>
            <p className="text-[#9CA3AF] text-sm leading-relaxed">
              Your trusted partner for everything in UAE! Making Dubai your permanent home.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#E8A620]">Services</h4>
            <ul className="space-y-2 text-[#9CA3AF] text-sm">
              <li>Golden Visa Services</li>
              <li>Wills & Estate Planning</li>
              <li>Company Formation</li>
              <li>Bank Account Setup</li>
              <li>Document Attestation</li>
              <li>Corporate Tax & Accounting</li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#E8A620]">Contact</h4>
            <ul className="space-y-2 text-[#9CA3AF] text-sm">
              <li>
                <a href="tel:+971585916890" className="hover:text-[#E8A620] transition-colors">
                  +971 58 591 6890
                </a>
              </li>
              <li>
                <a href="mailto:hello@northstarconsulting.ae" className="hover:text-[#E8A620] transition-colors">
                  hello@northstarconsulting.ae
                </a>
              </li>
              <li>
                <a 
                  href="https://wa.me/971585916890" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-[#25D366] transition-colors"
                >
                  WhatsApp: +971 58 591 6890
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4 text-[#E8A620]">Business Hours</h4>
            <ul className="space-y-2 text-[#9CA3AF] text-sm">
              <li>Monday - Saturday</li>
              <li className="text-white font-medium">8:00 AM - 4:00 PM</li>
              <li className="mt-4">Sunday: Closed</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-[#E8A620]/10 pt-8 text-center">
          <p className="text-[#9CA3AF] text-sm">
            © 2026 North Star Consulting. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Float Button
const WhatsAppFloat = () => {
  return (
    <a
      href="https://wa.me/971585916890"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float-btn"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform animate-pulse-gold"
    >
      <MessageCircle className="text-white" size={28} />
    </a>
  );
};

// Main App
function App() {
  return (
    <div className="App bg-[#050505] min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <StatsBar />
        <ServicesSection />
        <BenefitsSection />
        <WhyChooseUs />
        <EligibilitySection />
        <ContactSection />
        <FAQSection />
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

export default App;
